const validZones = new Map();
const partFormatters = new Map();

export const US_TIME_ZONES = [
  ['America/New_York', 'Eastern Time'], ['America/Chicago', 'Central Time'],
  ['America/Denver', 'Mountain Time'], ['America/Phoenix', 'Arizona'],
  ['America/Los_Angeles', 'Pacific Time'], ['America/Anchorage', 'Alaska Time'],
  ['Pacific/Honolulu', 'Hawaii Time'],
];

export function validTimeZone(value) {
  if (typeof value !== 'string' || !value.includes('/')) return null;
  if (validZones.has(value)) return validZones.get(value);
  try { new Intl.DateTimeFormat('en-US', { timeZone: value }); validZones.set(value, value); return value; }
  catch { validZones.set(value, null); return null; }
}

export function resolveShiftTimeZone(shift, facility, tenant) {
  for (const source of [shift, facility, tenant]) {
    const value = source?.timeZone || source?.timezone;
    // An explicitly invalid setting must be fixed, not silently overridden.
    if (value) return validTimeZone(value);
  }
  return null;
}

export function zonedParts(instant, timeZone) {
  if (!validTimeZone(timeZone)) throw new Error('An authoritative timezone is required.');
  if (!partFormatters.has(timeZone)) partFormatters.set(timeZone, new Intl.DateTimeFormat('en-CA', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }));
  const parts = partFormatters.get(timeZone).formatToParts(new Date(instant));
  return Object.fromEntries(parts.filter(p => p.type !== 'literal').map(p => [p.type, p.value]));
}

export function localDateTimeToInstant(value, timeZone) {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value || '');
  if (!match) throw new Error('Choose a valid local date and time.');
  const [, year, month, day, hour, minute, second = '00'] = match;
  const wall = Date.UTC(+year, +month - 1, +day, +hour, +minute, +second);
  const offsets = new Set();
  // Sample both sides of regional DST changes; Intl supplies the actual offsets.
  for (const delta of [-36, -12, 0, 12, 36]) {
    const sample = wall + delta * 3600000;
    const p = zonedParts(sample, timeZone);
    offsets.add(Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second) - sample);
  }
  const matches = [...offsets].map(offset => wall - offset).filter(instant => {
    const p = zonedParts(instant, timeZone);
    return p.year === year && p.month === month && p.day === day && p.hour === hour && p.minute === minute && p.second === second;
  });
  if (!matches.length) throw new Error('This local time does not exist in the selected timezone. Choose another time.');
  if (matches.length > 1) throw new Error('This local time occurs twice at daylight-saving change. Choose an unambiguous time.');
  return new Date(matches[0]);
}

export function formatShiftInstant(value, timeZone, options = {}) {
  if (!validTimeZone(timeZone)) return 'Timezone unavailable';
  const date = value?.toDate ? value.toDate() : new Date(value);
  if (!Number.isFinite(+date)) return 'Time unavailable';
  return new Intl.DateTimeFormat('en-US', {timeZone, ...options}).format(date);
}

export function zonedInputValue(value, timeZone) {
  const p = zonedParts(value, timeZone);
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

export function regionalCalendarBounds(now, timeZone) {
  const p = zonedParts(now, timeZone);
  const date = new Date(Date.UTC(+p.year, +p.month - 1, +p.day, 12));
  const midnight = d => +localDateTimeToInstant(`${d.toISOString().slice(0,10)}T00:00`, timeZone);
  const add = days => {const d = new Date(date); d.setUTCDate(d.getUTCDate()+days); return midnight(d);};
  return {
    today: add(0), tomorrow: add(1), afterTomorrow: add(2),
    weekStart: add(-date.getUTCDay()), weekEnd: add(7-date.getUTCDay()),
    monthStart: midnight(new Date(Date.UTC(+p.year,+p.month-1,1,12))),
    monthEnd: midnight(new Date(Date.UTC(+p.year,+p.month,1,12))),
  };
}

export function regionalShiftInterval(dateKey, startInput, endInput, timeZone) {
  const startClock = startInput.slice(11,16), endClock = endInput.slice(11,16);
  const next = new Date(`${dateKey}T12:00:00Z`);
  if (!Number.isFinite(+next)) throw new Error('Choose a valid shift date.');
  next.setUTCDate(next.getUTCDate()+1);
  const endDay = endClock <= startClock ? next.toISOString().slice(0,10) : dateKey;
  return {
    start: localDateTimeToInstant(`${dateKey}T${startClock}`, timeZone),
    end: localDateTimeToInstant(`${endDay}T${endClock}`, timeZone),
  };
}
